import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, UserCheck, FileText, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  const lastUpdated = "November 16, 2025";

  return (
    <article className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-blue-100 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center mb-6">
            <Shield className="h-12 w-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-xl text-blue-100">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8 rounded-r-lg">
          <p className="text-gray-700 leading-relaxed">
            At CyberEdge, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
          </p>
        </div>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Personal Information</h3>
            <p className="mb-4">We may collect personal information that you voluntarily provide to us when you:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Register for an account</li>
              <li>Subscribe to our newsletter</li>
              <li>Fill out a contact form</li>
              <li>Request a quote or consultation</li>
              <li>Participate in surveys or promotions</li>
            </ul>
            <p className="mb-4">This information may include:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Name and contact information (email address, phone number)</li>
              <li>Company name and business information</li>
              <li>Billing and payment information</li>
              <li>Professional credentials and qualifications</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Automatically Collected Information</h3>
            <p className="mb-4">When you visit our website, we automatically collect certain information about your device, including:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>IP address and browser type</li>
              <li>Operating system and device information</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website or source</li>
              <li>Clickstream data and navigation patterns</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <Eye className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Process your transactions and send related information</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Communicate about products, services, offers, and promotions</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations and protect our rights</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <Lock className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Data Security</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and authentication requirements</li>
              <li>Employee training on data protection</li>
              <li>Incident response and breach notification procedures</li>
            </ul>
            <p className="mb-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <UserCheck className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Your Rights and Choices</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Erasure:</strong> Request deletion of your personal information</li>
              <li><strong>Restriction:</strong> Request restriction of processing your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service provider</li>
              <li><strong>Objection:</strong> Object to processing of your personal information</li>
              <li><strong>Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
            </ul>
            <p className="mb-4">
              To exercise these rights, please contact us using the information provided below. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <FileText className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Data Retention</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When determining retention periods, we consider:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>The nature and sensitivity of the information</li>
              <li>The purposes for which we process the information</li>
              <li>Legal, regulatory, tax, accounting, or reporting requirements</li>
              <li>Our legitimate business interests</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              Our website may contain links to third-party websites and services. We are not responsible for the privacy practices or content of these third parties. We encourage you to read the privacy policies of any third-party sites you visit.
            </p>
            <p className="mb-4">
              We may use third-party service providers to help us operate our business and website or administer activities on our behalf. These third parties may have access to your personal information only to perform specific tasks on our behalf and are obligated not to disclose or use it for any other purpose.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete such information.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. We take appropriate safeguards to ensure your personal information remains protected in accordance with this Privacy Policy.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <p className="mb-2"><strong>CyberEdge Solutions</strong></p>
              <p className="mb-2">Email: info@cyberedgesolutions.com</p>
              <p className="mb-2">Phone: +91 6301644590</p>
              <p>Address: PJR Arcade, Kukatpally, Hyderabad, Telangana 500085</p>
            </div>
          </div>
        </section>

        <div className="flex flex-wrap gap-4 pt-8 border-t border-gray-200">
          <Link
            to="/terms"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Terms of Service
          </Link>
          <Link
            to="/cookies"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Cookie Policy
          </Link>
          <Link
            to="/gdpr"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            GDPR Compliance
          </Link>
        </div>
      </div>
    </article>
  );
}
